import React, { useMemo } from 'react';
import { Calculator } from 'lucide-react';
import FormattedNumberInput from '../FormattedNumberInput';
import { useLanguage } from '../../contexts/LanguageContext';
import { remainingMonthsFromPaymentCanadianAmortizing } from '../../utils/canadianMortgagePayment';
import {
  FINANCING_TYPE_EXISTING,
  FINANCING_TYPE_NONE,
} from '../../utils/dealPurpose';
import SectionShell from './SectionShell';

/**
 * Financing section for owned properties: in-place mortgage or none.
 */
export default function OwnedFinancingSection({ financing = {}, onChange }) {
  const { t } = useLanguage();
  const financingType = financing.financingType || FINANCING_TYPE_EXISTING;
  const isExisting = financingType === FINANCING_TYPE_EXISTING;

  const derived = useMemo(() => {
    const principal = parseFloat(financing.assumedLoanBalance) || 0;
    const ratePct = parseFloat(financing.mortgageRate) || 0;
    const payment = parseFloat(financing.assumptionStatedMonthlyPayment) || 0;
    if (principal <= 0 || payment <= 0 || ratePct <= 0) {
      return { months: null, paymentTooLow: false };
    }
    const months = remainingMonthsFromPaymentCanadianAmortizing(
      principal,
      ratePct / 100,
      payment,
    );
    return {
      months,
      paymentTooLow: months === null,
    };
  }, [
    financing.assumedLoanBalance,
    financing.mortgageRate,
    financing.assumptionStatedMonthlyPayment,
  ]);

  const handleChange = (field, value) => {
    const next = { ...financing, [field]: value };
    if (field === 'financingType' && value === FINANCING_TYPE_NONE) {
      next.assumedLoanBalance = '';
      next.assumptionStatedMonthlyPayment = '';
      next.assumptionTermEnd = '';
    }
    onChange(next, field);
  };

  return (
    <SectionShell
      title={t('ownedFinancing.title')}
      icon={Calculator}
      titleClassName="text-purple-600"
    >
      <div>
        <label className="block text-sm font-medium mb-1">{t('ownedFinancing.type')}</label>
        <select
          value={financingType}
          onChange={(e) => handleChange('financingType', e.target.value)}
          className="w-full border rounded p-2"
        >
          <option value={FINANCING_TYPE_EXISTING}>{t('ownedFinancing.type.existing')}</option>
          <option value={FINANCING_TYPE_NONE}>{t('ownedFinancing.type.none')}</option>
        </select>
      </div>

      {isExisting && (
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('ownedFinancing.loanBalance')}
            </label>
            <FormattedNumberInput
              value={financing.assumedLoanBalance || ''}
              onChange={(val) => handleChange('assumedLoanBalance', val)}
              className="w-full border rounded p-2"
              placeholder="0"
              type="currency"
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('ownedFinancing.monthlyPayment')}
              </label>
              <FormattedNumberInput
                value={financing.assumptionStatedMonthlyPayment || ''}
                onChange={(val) => handleChange('assumptionStatedMonthlyPayment', val)}
                className="w-full border rounded p-2"
                placeholder="0"
                type="currency"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('financing.contractualRate')}
              </label>
              <FormattedNumberInput
                value={financing.mortgageRate || ''}
                onChange={(val) => handleChange('mortgageRate', val)}
                className="w-full border rounded p-2"
                placeholder="5.5"
                type="percentage"
              />
              <p className="text-xs text-gray-500 mt-1">{t('financing.assumptionRateHint')}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('financing.assumptionTermEndOptional')}
            </label>
            <input
              type="date"
              value={financing.assumptionTermEnd || ''}
              onChange={(e) => handleChange('assumptionTermEnd', e.target.value)}
              className="w-full border rounded p-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">{t('financing.assumptionTermEndHint')}</p>
          </div>
          <div className="text-sm">
            <span className="text-gray-600">{t('financing.assumptionDerivedAmortMonths')}: </span>
            {derived.paymentTooLow ? (
              <span className="text-amber-700 font-medium">
                {t('financing.assumptionPaymentTooLow')}
              </span>
            ) : derived.months != null ? (
              <span className="font-semibold text-gray-900">
                {new Intl.NumberFormat(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                }).format(derived.months)}
              </span>
            ) : (
              <span className="text-gray-500">—</span>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('financing.assumptionDerivedAmortMonthsHint')}
            </p>
          </div>
        </div>
      )}

      {!isExisting && (
        <p className="mt-3 text-sm text-gray-600">{t('ownedFinancing.noneHint')}</p>
      )}
    </SectionShell>
  );
}
